import { matchBreakpoint } from '@chh/ui/js/matchBreakpoint';

const GAP = 40;
const desktop = matchBreakpoint('desktop');

// On desktop, asides are taken out of their block's height so long asides overflow into
// following blocks instead of opening a gap. Each aside group is then pushed down to clear
// the previous group in the same column, and any later block content that extends into an
// aside column (e.g. a full width image) is pushed down to clear the asides above it.
class AsideLayoutElement extends HTMLElement {
  connectedCallback() {
    this._resizeObserver = new ResizeObserver(this._layout);
    this._resizeObserver.observe(this);
    for (const aside of this.querySelectorAll('.block__asides > *')) {
      this._resizeObserver.observe(aside);
    }
    desktop.addEventListener('change', this._layout);
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    desktop.removeEventListener('change', this._layout);
  }

  _layout = () => {
    const blocks = this.querySelectorAll('.block');

    this.style.paddingBottom = '';
    for (const block of blocks) {
      block.style.paddingTop = '';
      for (const group of block.querySelectorAll(':scope > .block__asides')) {
        group.style.top = '';
      }
    }

    this.classList.toggle('is-active', desktop.matches);
    if (!desktop.matches) return;

    const columns = new Map();

    for (const block of blocks) {
      const groups = [...block.querySelectorAll(':scope > .block__asides')];
      const content = [...block.children].filter((child) => !groups.includes(child));

      let clearance = 0;
      for (const column of columns.values()) {
        for (const child of content) {
          const rect = child.getBoundingClientRect();
          if (rect.right > column.left && rect.left < column.right) {
            clearance = Math.max(clearance, column.bottom + GAP - rect.top);
          }
        }
      }
      if (clearance > 0) {
        block.style.paddingTop = `${clearance}px`;
      }

      for (const group of groups) {
        const side = group.classList.contains('block__asides--left') ? 'left' : 'right';
        const rect = group.getBoundingClientRect();
        const previous = columns.get(side);
        const offset = previous ? previous.bottom + GAP - rect.top : 0;
        if (offset > 0) {
          group.style.top = `${offset}px`;
        }
        columns.set(side, {
          left: rect.left,
          right: rect.right,
          bottom: group.lastElementChild.getBoundingClientRect().bottom,
        });
      }
    }

    const bottom = Math.max(...[...columns.values()].map((column) => column.bottom));
    const overflow = bottom - this.getBoundingClientRect().bottom;
    if (overflow > 0) {
      this.style.paddingBottom = `${overflow}px`;
    }
  };
}

customElements.define('chh-aside-layout', AsideLayoutElement);

export default AsideLayoutElement;
